import BigNumber from 'bignumber.js';
import { defineStore } from 'pinia';
import { useCurrencyConverter } from '~/composables/useCurrencyConverter';
import {
  ERaceLabel,
  ERaceMechanics,
  ERaceStatus,
  type IMinBet,
  type ITournamentExtended,
  type ITournamentWithStatus,
} from '~/types/races';
import { DAYS_AMOUNT_TO_SHOW_FINISHED_RACES } from '~/utils/constants';
import { DAY_MILLISECONDS } from '~/utils/date/constants';
import { useStoreRoot } from '../abstract/root';
import { useStoreComponents } from '../strapi/components';
import { useStoreCurrencyMenu } from '../strapi/currencyMenu';
import { useStoreRaces } from '../strapi/races';
import { softswiss } from '../transport/softswiss';
import { useStoreSsPlayer } from './player';
import type {
  IPlayerTournamentData,
  ISsTournamentResponse,
  ISsTournamentStatus,
} from '~/types/softswiss/ssTournaments';
import type { IHttpRequestOptions } from '~/types/transport/http';

export const useStoreSsTournaments = defineStore('ssTournaments', {
  state: () => ({
    tournaments: [] as ISsTournamentResponse[],
    tournamentStatus: [] as ISsTournamentStatus[],
    tournamentStatusCache: [] as ISsTournamentStatus[],
    tournamentsStatuses: [] as { status: null | boolean | undefined | string; frontId: string }[],
    currentTournamentID: NaN,
    requestPromise: null as Promise<any> | null,
    requestStatusesAcive: false,
  }),

  getters: {
    tournamentsWithStatuses(): ITournamentWithStatus[] {
      const dateNow = Date.now();

      return this.tournaments.map((t) => {
        let status: ERaceStatus;

        if (t.end_at && new Date(t.end_at).getTime() <= dateNow) {
          status = ERaceStatus.finished;
        } else if (t.start_at && new Date(t.start_at).getTime() >= dateNow) {
          status = ERaceStatus.scheduled;
        } else {
          status = ERaceStatus.active;
        }

        return { ...t, status };
      });
    },
    tournamentsExtended(): ITournamentExtended[] {
      const tournamentsExtended: ITournamentExtended[] = [];

      this.tournamentsWithStatuses.forEach((t) => {
        const raceById = useStoreRaces().activeRaces.find((r) => r.ssId === t.frontend_identifier);

        if (raceById) {
          tournamentsExtended.push({
            tournamentData: t,
            raceData: raceById,
          });
        }
      });

      return tournamentsExtended.sort(
        (a, b) => Number(a.raceData.position) - Number(b.raceData.position)
      );
    },
    tournamentsActive(): ITournamentExtended[] {
      return this.tournamentsExtended.filter(
        (tournament) => tournament.tournamentData.status === ERaceStatus.active
      );
    },
    tournamentsScheduled(): ITournamentExtended[] {
      return this.tournamentsExtended.filter(
        (tournament) => tournament.tournamentData.status === ERaceStatus.scheduled
      );
    },
    tournamentsFinished(): ITournamentExtended[] {
      return this.tournamentsExtended.filter((tournament) => {
        if (!tournament.tournamentData.end_at) {
          return false;
        }

        const finishDayMS = new Date(tournament.tournamentData.end_at);
        // @ts-ignore
        const finishDayDifferenceFromNow = Date.now() - finishDayMS;
        const endedLessThanReqiredDays =
          finishDayDifferenceFromNow < DAYS_AMOUNT_TO_SHOW_FINISHED_RACES * DAY_MILLISECONDS;
        const isFinishedStatus = tournament.tournamentData.status === ERaceStatus.finished;

        return endedLessThanReqiredDays && isFinishedStatus;
      });
    },
  },

  actions: {
    async loadTournaments(
      options?: Omit<IHttpRequestOptions, 'path'> & { includeHistory?: boolean }
    ): Promise<ISsTournamentResponse[] | null> {
      this.requestPromise = softswiss()
        .get({ ...options, path: `/api/tournaments` })
        .finally(() => (this.requestPromise = null));

      const response: ISsTournamentResponse[] | null = await this.requestPromise;

      if (response && response?.length) {
        const tournaments = [...response].map((tournament) => {
          return {
            ...tournament,
            top_players: tournament.top_players?.map((player) => {
              return {
                ...player,
                isCurrentUser: false,
              };
            }),
          };
        });

        if (options?.includeHistory) {
          const storeRaces = useStoreRaces();
          const raceIds = storeRaces.activeRaces
            .filter((p) => p.historicalTournamentsId)
            .map((t) => Number(t.historicalTournamentsId));

          const tournamentIds = tournaments.map((t) => t.id);
          const historicalTournamentsPromises = raceIds
            .map((id: number) => {
              if (!tournamentIds.includes(id)) {
                return softswiss()
                  .get({
                    path: `/api/tournaments/${id}`,
                    force: true,
                  })
                  .catch(() => null);
              }
              return null;
            })
            .filter((el) => el !== null);

          const historicalTournaments = await Promise.all(historicalTournamentsPromises);

          historicalTournaments.forEach((historicalTournament: any) => {
            if (historicalTournament) {
              tournaments.push({
                ...historicalTournament,
                top_players: historicalTournament.top_players?.map(
                  (player: IPlayerTournamentData) => ({
                    ...player,
                    isCurrentUser: false,
                  })
                ),
              });
            }
          });
        }

        this.tournaments = tournaments;
        this.currentTournamentID = NaN;
        return response;
      } else {
        return null;
      }
    },

    async loadTournamentById(id: number) {
      const response = await softswiss().get({
        path: `/api/tournaments/${id}`,
        force: true,
      });

      if (response) {
        this.tournaments.push({
          ...response,
          top_players: response.top_players?.map((player: IPlayerTournamentData) => ({
            ...player,
            isCurrentUser: false,
          })),
        });

        return response;
      } else {
        return null;
      }
    },

    async loadTournamentHistory() {
      const tournamentIds = this.tournamentsExtended.map((t) => t.tournamentData.id);
      const storeRaces = useStoreRaces();
      const raceIds = storeRaces.activeRaces
        .filter((p) => p.historicalTournamentsId)
        .map((t) => Number(t.historicalTournamentsId));

      await Promise.all(
        raceIds
          .map((id: number) => {
            if (!tournamentIds.includes(id)) {
              return this.loadTournamentById(id);
            }
            return null;
          })
          .filter((el) => el !== null)
      );
    },

    async confirmPlayerParticipation(id: number) {
      const response = await softswiss().post({
        path: `/api/tournaments/${id}/confirm`,
        payload: {
          confirmed: true,
        },
      });

      return response;
    },
    async getStatusPlayer(id: number, tournamentExtended: ITournamentExtended) {
      const isUserJoinedTournament = this.findIfUserJoinedTournament(tournamentExtended);
      const cachedDataStatus = this.tournamentStatusCache.find((s) => s.tournament_id === id);

      if (isUserJoinedTournament && cachedDataStatus) {
        this.tournamentStatus = [cachedDataStatus];

        return cachedDataStatus;
      }

      const response = (await softswiss().get({
        path: `/api/tournaments/${id}/status`,
        force: true,
      })) as ISsTournamentStatus;

      if (response) {
        this.tournamentStatus = [response];
        this.tournamentStatusCache.push(response);
      } else {
        this.tournamentStatus = [];
      }
      return response;
    },
    async getTournamentForPlayer() {
      const response = await softswiss().get({
        path: '/api/tournaments/player',
        force: true,
      });

      return response;
    },
    /**
     * Add tournament status from websocket
     */
    addTournamentStatus(data: ISsTournamentStatus[]) {
      this.tournamentStatus = data;
      const topPlayers = this.tournaments.find(
        (t) => t.id === this.currentTournamentID
      )?.top_players;
      const currentPlace = topPlayers?.find((p) => p.isCurrentUser)?.award_place;
      const newPlace = data.find((n) => n.tournament_id === this.currentTournamentID)?.award_place;

      if (currentPlace !== newPlace && newPlace && topPlayers) {
        const myIndexInTop = topPlayers.findIndex((t) => t.isCurrentUser);
        const myNewIndexInTop = newPlace - 1;
        const playerData = topPlayers.splice(myIndexInTop, 1);
        topPlayers?.splice(myNewIndexInTop, 0, playerData[0]);
      }
    },
    setCurrentUserInTournament(tournamentID: number) {
      if (tournamentID === this.currentTournamentID) {
        return;
      }

      this.currentTournamentID = tournamentID;

      const tournament = this.tournaments?.find((t) => t.id === tournamentID);
      const status = this.tournamentStatus?.find((s) => s.tournament_id === tournamentID);

      if (tournament && status) {
        const player = tournament.top_players?.find((p) => p.award_place === status.award_place);

        if (player) {
          player.isCurrentUser = true;
        }
      }
    },
    async loadPlayerStatusInTournament(tournamentExtended: ITournamentExtended | undefined) {
      if (useStoreSsPlayer().isPlayerAuthorised && tournamentExtended?.tournamentData.id) {
        try {
          await this.getStatusPlayer(tournamentExtended.tournamentData.id, tournamentExtended);
          this.setCurrentUserInTournament(tournamentExtended.tournamentData.id);
        } catch (e) {
          console.warn('No user in this tournament');
        }
      }
    },
    async loadPlayerStatusesInTournaments() {
      if (
        useStoreSsPlayer().isPlayerAuthorised &&
        this.tournamentsActive.length &&
        !this.requestStatusesAcive
      ) {
        this.requestStatusesAcive = true;
        const responseStatusesList = (await softswiss().get({
          path: `/api/tournaments/statuses`,
          force: true,
        })) as ISsTournamentStatus[];
        for (let i = 0; i < this.tournamentsActive.length; i++) {
          const tournament = this.tournamentsActive[i];
          if (tournament.tournamentData.id && tournament.tournamentData.frontend_identifier) {
            const response = responseStatusesList.find(
              (t) => t.tournament_id === tournament.tournamentData.id
            );
            const isTournamentDontNeedConfirmation =
              !tournament.tournamentData.user_confirmation_required &&
              useStoreSsPlayer().isPlayerAuthorised;
            const statusInArray = this.tournamentsStatuses.find(
              (s) => s.frontId === tournament.tournamentData.frontend_identifier
            );
            if (statusInArray) {
              statusInArray.status = response?.user_confirmed || isTournamentDontNeedConfirmation;
            } else {
              this.tournamentsStatuses.push({
                status: response?.user_confirmed || isTournamentDontNeedConfirmation,
                frontId: tournament.tournamentData.frontend_identifier,
              });
            }
          }
        }
        this.requestStatusesAcive = false;
      }
    },
    async joinTournament(tournamentExtended: ITournamentExtended | undefined) {
      if (
        tournamentExtended?.tournamentData.user_confirmation_required &&
        tournamentExtended.tournamentData.id
      ) {
        await this.confirmPlayerParticipation(tournamentExtended.tournamentData.id);
        await this.loadTournaments();
        this.loadPlayerStatusInTournament(tournamentExtended);
        this.loadPlayerStatusesInTournaments();
      }
    },
    findIfUserJoinedTournament(tournamentExtended: ITournamentExtended) {
      const isInTopPlayers = tournamentExtended.tournamentData.top_players?.some(
        (player) => player.isCurrentUser
      );
      const tournamentStatus = this.tournamentsStatuses?.filter(
        (t) => t.frontId === tournamentExtended.tournamentData.frontend_identifier
      );

      const isUserJoinConfirmed = tournamentStatus[0]?.status;

      return !!isInTopPlayers || !!isUserJoinConfirmed;
    },
    findIfJoinButtonShown(tournamentExtended?: ITournamentExtended) {
      if (!tournamentExtended) {
        return false;
      }

      const isUserJoinedTournament = this.findIfUserJoinedTournament(tournamentExtended);
      const isTournamentNeedConfirmation =
        !isUserJoinedTournament && tournamentExtended.tournamentData.user_confirmation_required;

      return !useStoreSsPlayer().isPlayerAuthorised || !!isTournamentNeedConfirmation;
    },
    findIfSuccessMessageShown(tournamentExtended?: ITournamentExtended) {
      if (!tournamentExtended) {
        return false;
      }

      const isUserJoinedTournament = this.findIfUserJoinedTournament(tournamentExtended);
      const isTournamentDontNeedConfirmation =
        !tournamentExtended.tournamentData.user_confirmation_required &&
        useStoreSsPlayer().isPlayerAuthorised;

      return isUserJoinedTournament || isTournamentDontNeedConfirmation;
    },
    getUserPlace(tournamentExtended?: ITournamentExtended) {
      if (!tournamentExtended) {
        return null;
      }

      const tournamentStatus = this.tournamentStatus?.find(
        (t) => t.tournament_id && tournamentExtended.tournamentData
      );

      return tournamentStatus?.award_place || null;
    },
    getMinimumBetValue(minimumBet?: IMinBet) {
      if (!minimumBet) {
        return null;
      }

      const { convertToFormattedAmount } = useCurrencyConverter();
      const userCurrency = useStoreSsPlayer().isPlayerAuthorised
        ? useStoreRoot().userCurrency
        : 'USD';

      let currency = userCurrency;
      // @ts-ignore
      let amount = minimumBet[userCurrency];

      if (!amount && amount !== 0) {
        amount = minimumBet.USD;
        currency = 'USD';
      }

      const precision =
        useStoreCurrencyMenu().activeCurrencies.find((c) => c.currencyCode === currency)
          ?.precision || 0;
      const amountCents = new BigNumber(amount).shiftedBy(precision).toNumber();
      const { formattedAmount, formattedCurrency } = convertToFormattedAmount(
        amountCents,
        currency
      );

      if (amount || amount === 0) {
        return `${formattedAmount} ${formattedCurrency}`;
      }

      return null;
    },
    getRaceLabelTitle(raceLabel?: ERaceLabel) {
      if (!raceLabel) {
        return '';
      }

      const storeComponents = useStoreComponents();

      switch (raceLabel) {
        case ERaceLabel.Weekly:
          return storeComponents.races.raceLabelWeeklyTitle;
        case ERaceLabel.Daily:
          return storeComponents.races.raceLabelDailyTitle;
        case ERaceLabel.Exclusive:
          return storeComponents.races.raceLabelExclusiveTitle;
        case ERaceLabel.Event:
          return storeComponents.races.raceLabelEventTitle;
        case ERaceLabel.Partner:
          return storeComponents.races.raceLabelPartnerTitle;
        default:
          return '';
      }
    },
    getRaceLabelColor(raceLabel?: ERaceLabel) {
      if (!raceLabel) {
        return '';
      }

      switch (raceLabel) {
        case ERaceLabel.Weekly:
          return 'blue';
        case ERaceLabel.Daily:
          return 'yellow';
        case ERaceLabel.Exclusive:
          return 'pink';
        case ERaceLabel.Partner:
          return 'green';
        case ERaceLabel.Event:
          return 'blue';
        default:
          return '';
      }
    },
    getRaceTimerTitle(raceStatus?: ERaceStatus) {
      if (!raceStatus) {
        return '';
      }

      const storeComponents = useStoreComponents();

      switch (raceStatus) {
        case ERaceStatus.finished:
          return storeComponents.timer.finishedTitle;
        case ERaceStatus.scheduled:
          return storeComponents.timer.willStartTitle;
        default:
          return storeComponents.timer.leftTitle;
      }
    },
    getMechanicsData(raceMechanics?: ERaceMechanics) {
      if (!raceMechanics) {
        return {
          title: '',
          description: '',
        };
      }

      const storeComponents = useStoreComponents();

      switch (raceMechanics) {
        case ERaceMechanics.Bet:
          return {
            title: storeComponents.races.mechanicsBetTitle,
            description: storeComponents.races.mechanicsBetDescriptionText,
          };
        case ERaceMechanics.Points:
          return {
            title: storeComponents.races.mechanicsPointsTitle,
            description: storeComponents.races.mechanicsPointsDescriptionText,
          };
        case ERaceMechanics.Rate:
          return {
            title: storeComponents.races.mechanicsRateTitle,
            description: storeComponents.races.mechanicsRateDescriptionText,
          };
        case ERaceMechanics.Spin:
          return {
            title: storeComponents.races.mechanicsSpinTitle,
            description: storeComponents.races.mechanicsSpinDescriptionText,
          };
        case ERaceMechanics.Win:
          return {
            title: storeComponents.races.mechanicsWinTitle,
            description: storeComponents.races.mechanicsWinDescriptionText,
          };
        default:
          return {
            title: '',
            description: '',
          };
      }
    },
    resetState() {
      this.tournamentStatus = [];
      this.tournamentsStatuses = [];
    },
  },
});
